import { VariantsOptionsEntity } from 'mk2/apps/strollers/schemas';
import { AST, ASTAdditionalDataFromServer } from 'mk2/apps/wiki/schemas';
import { APIFailureAction, APIRequestAction, APISuccessAction } from 'mk2/helpers/action';
import { NormalizedResponse } from 'mk2/helpers/api';

interface LinkedObject {
    id: number;
    createdAt: string;
}

export interface ForumPostLinkedObject extends LinkedObject {
    key: 'forum.thread';
    title: string;
    slug: string;
    topicSlug: string;
    isCounselling: boolean;
}

export type LinkedObjects = ForumPostLinkedObject[];

///////////////////////////////////////////////////////////////////
// Stroller detail load

export const STROLLERS_DETAIL_FETCH_TRIGGER = 'STROLLERS_DETAIL_FETCH_TRIGGER';
export interface StrollersDetailFetchTriggerAction {
    readonly type: typeof STROLLERS_DETAIL_FETCH_TRIGGER;
    readonly slug: string;
    readonly variantId?: number;
}
export const STROLLERS_DETAIL_FETCH_REQUEST = 'STROLLERS_DETAIL_FETCH_REQUEST';
export interface StrollersDetailFetchRequestAction extends APIRequestAction {
    readonly type: typeof STROLLERS_DETAIL_FETCH_REQUEST;
    readonly slug: string;
    readonly variantId?: number;
}
export const STROLLERS_DETAIL_FETCH_SUCCESS = 'STROLLERS_DETAIL_FETCH_SUCCESS';
export interface StrollersDetailFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        stroller: number;
        strollerReviews: number[];
        strollerPendingReviews: number[];
        strollerAlikeStrollers: number[];
        strollerQuestions: number[];
        similars: number[];
        strollersOfThisBrand: number[];
        eshopProducts: number[];
        cheapestOldProduct: number;
        buyingGuide: number;
        counselling: number;
        articles: number[];
        onProfile: boolean;
        hasMoreSimilars: boolean;
        countOfSimilars: number;
        oldProductsCount: number;
        linkedObjects: LinkedObjects;
        patterns: number[];
        variant: number;
        variantsIds: number[];
        variantsOptions: VariantsOptionsEntity;
        baseVariantId: number;
        variantId: number;
        strollerPhotos: number[];
        allStrollerPhotos: number[];
        variantsPhotos: number[];
        relatedStrollersPreviewPhotos: any;
        descriptionAST: AST;
        descriptionASTAdditionalData: ASTAdditionalDataFromServer;
        descriptionPhotos: number[];
        eshopProductsWidget: number[];
    };
}
export interface StrollersDetailFetchSuccessAction extends APISuccessAction<StrollersDetailFetchNormalizedResponse> {
    readonly type: typeof STROLLERS_DETAIL_FETCH_SUCCESS;
    readonly slug: string;
    readonly variantId?: number;
}
export const STROLLERS_DETAIL_FETCH_FAILURE = 'STROLLERS_DETAIL_FETCH_FAILURE';
export interface StrollersDetailFetchFailureAction extends APIFailureAction {
    readonly type: typeof STROLLERS_DETAIL_FETCH_FAILURE;
    readonly slug: string;
    readonly variantId?: number;
}

export const strollersDetailFetchTrigger =
    (slug: string, variantId?: number): StrollersDetailFetchTriggerAction => ({
        type: STROLLERS_DETAIL_FETCH_TRIGGER, slug, variantId,
    });
export const strollersDetailFetch = {
    request: (slug: string, variantId?: number): StrollersDetailFetchRequestAction => ({
        type: STROLLERS_DETAIL_FETCH_REQUEST,
        slug,
        variantId,
    }),
    success: (slug: string, response: StrollersDetailFetchNormalizedResponse, variantId?: number): StrollersDetailFetchSuccessAction => ({
        type: STROLLERS_DETAIL_FETCH_SUCCESS, slug, variantId, response,
    }),
    failure: (slug: string, error: any, variantId?: number): StrollersDetailFetchFailureAction => ({
        type: STROLLERS_DETAIL_FETCH_FAILURE, slug, variantId, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Load more similars

export const STROLLERS_DETAIL_LOAD_MORE_SIMILARS_TRIGGER = 'STROLLERS_DETAIL_LOAD_MORE_SIMILARS_TRIGGER';
export interface StrollersDetailLoadMoreSimilarsTriggerAction {
    readonly type: typeof STROLLERS_DETAIL_LOAD_MORE_SIMILARS_TRIGGER;
    readonly slug: string;
    readonly offset: number;
}
export const STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST = 'STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST';
export interface StrollersDetailLoadMoreSimilarsRequestAction extends APIRequestAction {
    readonly type: typeof STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST;
    readonly slug: string;
    readonly offset: number;
}
export interface StrollersDetailLoadMoreSimilarsNormalizedResponse extends NormalizedResponse {
    readonly result: {
        similars: number[];
        hasMoreSimilars: boolean;
        countOfSimilars: number;
        strollersPreviewPhotoMap: Record<number, number>;
        strollersPhotos: number[];
    };
}
export const STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS = 'STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS';
export interface StrollersDetailLoadMoreSimilarsSuccessAction extends APISuccessAction<StrollersDetailLoadMoreSimilarsNormalizedResponse> {
    readonly type: typeof STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS;
    readonly slug: string;
    readonly offset: number;
}
export const STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE = 'STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE';
export interface StrollersDetailLoadMoreSimilarsFailureAction extends APIFailureAction {
    readonly type: typeof STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE;
    readonly slug: string;
    readonly offset: number;
}

export const strollersDetailLoadMoreSimilarsTrigger =
    (slug: string, offset: number): StrollersDetailLoadMoreSimilarsTriggerAction => ({
    type: STROLLERS_DETAIL_LOAD_MORE_SIMILARS_TRIGGER, slug, offset,
});
export const strollersDetailLoadMoreSimilarsApi = {
    request: (slug: string, offset: number): StrollersDetailLoadMoreSimilarsRequestAction => ({
        type: STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST, slug, offset,
    }),
    success: (slug: string, offset: number, response: StrollersDetailLoadMoreSimilarsNormalizedResponse): StrollersDetailLoadMoreSimilarsSuccessAction => ({
        type: STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS, slug, offset, response,
    }),
    failure: (slug: string, offset: number, error: any): StrollersDetailLoadMoreSimilarsFailureAction => ({
        type: STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE, slug, offset, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Add to profile

export interface StrollersDetailAddToProfileApiAction {
    readonly slug: string;
    readonly strollerId: number;
    readonly username: string;
}

export const STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST = 'STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST';

export interface StrollersDetailAddToProfileApiRequestAction
    extends StrollersDetailAddToProfileApiAction,
        APIRequestAction {
    readonly type: typeof STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST;
}

export interface StrollersDetailAddToProfileApiNormalizedResponse extends NormalizedResponse {
    readonly result: {};
}

export const STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS = 'STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS';

export interface StrollersDetailAddToProfileApiSuccessAction
    extends StrollersDetailAddToProfileApiAction,
        APISuccessAction<StrollersDetailAddToProfileApiNormalizedResponse> {
    readonly type: typeof STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS;
}

export const STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE = 'STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE';

export interface StrollersDetailAddToProfileApiFailureAction
    extends StrollersDetailAddToProfileApiAction,
        APIFailureAction {
    readonly type: typeof STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE;
}

export const strollersDetailAddToProfileApi = {
    request: (slug: string, strollerId: number, username: string): StrollersDetailAddToProfileApiRequestAction => ({
        type: STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST,
        strollerId,
        username,
        slug,
    }),
    success: (
        slug: string,
        strollerId: number,
        username: string,
        response: StrollersDetailAddToProfileApiNormalizedResponse,
    ): StrollersDetailAddToProfileApiSuccessAction => ({
        type: STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS,
        slug,
        strollerId,
        username,
        response,
    }),
    failure: (
        slug: string,
        strollerId: number,
        username: string,
        error: any,
    ): StrollersDetailAddToProfileApiFailureAction => ({
        type: STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE,
        slug,
        strollerId,
        username,
        error,
    }),
};

export const STROLLERS_DETAIL_ADD_TO_PROFILE = 'STROLLERS_DETAIL_ADD_TO_PROFILE';

export interface StrollersDetailAddToProfileAction extends StrollersDetailAddToProfileApiAction {
    readonly type: typeof STROLLERS_DETAIL_ADD_TO_PROFILE;
}

export const strollersDetailAddToProfile = (
    slug: string,
    strollerId: number,
    username: string,
): StrollersDetailAddToProfileAction => ({
    type: STROLLERS_DETAIL_ADD_TO_PROFILE,
    slug,
    strollerId,
    username,
});

///////////////////////////////////////////////////////////////////
// Remove from profile

export interface StrollersDetailRemoveFromProfileApiAction {
    readonly slug: string;
    readonly strollerId: number;
    readonly username: string;
}

export const STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST = 'STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST';

export interface StrollersDetailRemoveFromProfileApiRequestAction
    extends StrollersDetailRemoveFromProfileApiAction,
        APIRequestAction {
    readonly type: typeof STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST;
}

export interface StrollersDetailRemoveFromProfileApiNormalizedResponse extends NormalizedResponse {
    readonly result: {};
}

export const STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS = 'STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS';

export interface StrollersDetailRemoveFromProfileApiSuccessAction
    extends StrollersDetailRemoveFromProfileApiAction,
        APISuccessAction<StrollersDetailRemoveFromProfileApiNormalizedResponse> {
    readonly type: typeof STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS;
}

export const STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE = 'STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE';

export interface StrollersDetailRemoveFromProfileApiFailureAction
    extends StrollersDetailRemoveFromProfileApiAction,
        APIFailureAction {
    readonly type: typeof STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE;
}

export const strollersDetailRemoveFromProfileApi = {
    request: (
        slug: string,
        strollerId: number,
        username: string,
    ): StrollersDetailRemoveFromProfileApiRequestAction => ({
        type: STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST,
        slug,
        strollerId,
        username,
    }),
    success: (
        slug: string,
        strollerId: number,
        username: string,
        response: StrollersDetailRemoveFromProfileApiNormalizedResponse,
    ): StrollersDetailRemoveFromProfileApiSuccessAction => ({
        type: STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS,
        slug,
        strollerId,
        username,
        response,
    }),
    failure: (
        slug: string,
        strollerId: number,
        username: string,
        error: any,
    ): StrollersDetailRemoveFromProfileApiFailureAction => ({
        type: STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE,
        slug,
        strollerId,
        username,
        error,
    }),
};

export const STROLLERS_DETAIL_REMOVE_FROM_PROFILE = 'STROLLERS_DETAIL_REMOVE_FROM_PROFILE';

export interface StrollersDetailRemoveFromProfileAction extends StrollersDetailRemoveFromProfileApiAction {
    readonly type: typeof STROLLERS_DETAIL_REMOVE_FROM_PROFILE;
}

export const strollersDetailRemoveFromProfile = (
    slug: string,
    strollerId: number,
    username: string,
): StrollersDetailRemoveFromProfileAction => ({
    type: STROLLERS_DETAIL_REMOVE_FROM_PROFILE,
    slug,
    strollerId,
    username,
});

///////////////////////////////////////////////////////////////////
// Delete stroller

export const STROLLERS_DETAIL_DELETE_STROLLER_TRIGGER = 'STROLLERS_DETAIL_DELETE_STROLLER_TRIGGER';
export interface StrollersDetailDeleteStrollerTriggerAction {
    readonly type: typeof STROLLERS_DETAIL_DELETE_STROLLER_TRIGGER;
    readonly strollerId: number;
}

export const STROLLERS_DETAIL_DELETE_STROLLER_REQUEST = 'STROLLERS_DETAIL_DELETE_STROLLER_REQUEST';
export interface StrollersDetailDeleteStrollerRequestAction extends APIRequestAction {
    readonly type: typeof STROLLERS_DETAIL_DELETE_STROLLER_REQUEST;
    readonly strollerId: number;
}
export interface StrollersDetailDeleteStrollerNormalizedResponse extends NormalizedResponse {
    readonly result: { stroller: number; };
}
export const STROLLERS_DETAIL_DELETE_STROLLER_SUCCESS = 'STROLLERS_DETAIL_DELETE_STROLLER_SUCCESS';
export interface StrollersDetailDeleteStrollerSuccessAction extends APISuccessAction<StrollersDetailDeleteStrollerNormalizedResponse> {
    readonly type: typeof STROLLERS_DETAIL_DELETE_STROLLER_SUCCESS;
    readonly strollerId: number;
}
export const STROLLERS_DETAIL_DELETE_STROLLER_FAILURE = 'STROLLERS_DETAIL_DELETE_STROLLER_FAILURE';
export interface StrollersDetailDeleteStrollerFailureAction extends APIFailureAction {
    readonly type: typeof STROLLERS_DETAIL_DELETE_STROLLER_FAILURE;
    readonly strollerId: number;
}

export const strollersDetailDeleteStrollerTrigger =
    (strollerId: number): StrollersDetailDeleteStrollerTriggerAction => ({
        type: STROLLERS_DETAIL_DELETE_STROLLER_TRIGGER, strollerId,
    });
export const strollersDetailDeleteStrollerApi = {
    request: (strollerId: number): StrollersDetailDeleteStrollerRequestAction => ({
        type: STROLLERS_DETAIL_DELETE_STROLLER_REQUEST, strollerId,
    }),
    success: (strollerId: number, response: StrollersDetailDeleteStrollerNormalizedResponse): StrollersDetailDeleteStrollerSuccessAction => ({
        type: STROLLERS_DETAIL_DELETE_STROLLER_SUCCESS, strollerId, response,
    }),
    failure: (strollerId: number, error: any): StrollersDetailDeleteStrollerFailureAction => ({
        type: STROLLERS_DETAIL_DELETE_STROLLER_FAILURE, strollerId, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Change variant feature value

export const STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER = 'STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER';
export interface StrollersDetailChangeVariantFeatureValueTriggerAction {
    readonly type: typeof STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER;
    readonly slug: string;
    readonly configFeatureName: string;
    readonly value: string;
    readonly linkedId: number;
}

export const STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_REQUEST = 'STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_REQUEST';
export interface StrollersDetailChangeVariantFeatureValueRequestAction extends APIRequestAction {
    readonly type: typeof STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_REQUEST;
    readonly linkedId: number;
}
export interface StrollersDetailChangeVariantFeatureValueNormalizedResponse extends NormalizedResponse {
    readonly result: {
        variantsOptions: VariantsOptionsEntity;
        variant: number; };
}
export const STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS = 'STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS';
export interface StrollersDetailChangeVariantFeatureValueSuccessAction extends APISuccessAction<StrollersDetailChangeVariantFeatureValueNormalizedResponse> {
    readonly type: typeof STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS;
    readonly slug: string;
    readonly linkedId: number;
}
export const STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_FAILURE = 'STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_FAILURE';
export interface StrollersDetailChangeVariantFeatureValueFailureAction extends APIFailureAction {
    readonly type: typeof STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_FAILURE;
    readonly linkedId: number;
}

export const strollersDetailChangeVariantFeatureValueTrigger =
    (slug: string, configFeatureName: string, value: string, linkedId: number): StrollersDetailChangeVariantFeatureValueTriggerAction => ({
        type: STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER, slug, configFeatureName, value, linkedId,
    });
export const strollersDetailChangeVariantFeatureValueApi = {
    request: (linkedId: number): StrollersDetailChangeVariantFeatureValueRequestAction => ({
        type: STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_REQUEST, linkedId,
    }),
    success: (linkedId: number, slug: string, response: StrollersDetailChangeVariantFeatureValueNormalizedResponse): StrollersDetailChangeVariantFeatureValueSuccessAction => ({
        type: STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS, linkedId, slug, response,
    }),
    failure: (linkedId: number, error: any): StrollersDetailChangeVariantFeatureValueFailureAction => ({
        type: STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_FAILURE, linkedId, error,
    }),
};
